import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Panel flotante para los selectores de fecha y hora.
 *
 * Va en un portal al body: dentro del modal, que tiene overflow, un panel en
 * posición absoluta quedaría recortado. Al flotar se superpone al contenido en
 * vez de empujarlo.
 *
 * Se recoloca si se hace scroll o cambia el tamaño, y se sube encima del campo
 * cuando abajo no cabe.
 */
function PickerPopover({ anchorRef, open, onClose, children, minWidth = 0 }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    const height = panel.offsetHeight;
    const width = Math.max(rect.width, minWidth);
    const GAP = 8;

    const fitsBelow = window.innerHeight - rect.bottom >= height + GAP;
    const fitsAbove = rect.top >= height + GAP;
    const top = !fitsBelow && fitsAbove ? rect.top - height - GAP : rect.bottom + GAP;

    // No dejamos que se salga por los lados
    const left = Math.min(
      Math.max(GAP, rect.left),
      Math.max(GAP, window.innerWidth - width - GAP)
    );

    setPos({ top, left, width });
  }, [anchorRef, minWidth]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    // `true` para capturar también el scroll del modal, no solo el de la ventana
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    const onPointerDown = (e) => {
      const inPanel = panelRef.current?.contains(e.target);
      const inAnchor = anchorRef.current?.contains(e.target);
      if (!inPanel && !inAnchor) onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, place, onClose, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="dyd-popover"
      style={{
        top: pos ? pos.top : -9999,
        left: pos ? pos.left : -9999,
        minWidth: pos ? pos.width : minWidth,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body
  );
}

export default PickerPopover;
